import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { behaviorOfType } from "@nh/shared";
import {
  FormSection,
  FormSectionStack,
  NumberField,
} from "@/features/admin/components";
import { CatalogSelectField } from "@/features/admin/catalog";
import { ProductActivationField } from "../ProductActivationField";
import { ProductPackageItems } from "../ProductPackageItems";
import { ProductPromotionFields } from "../ProductPromotionFields";
import { SlugField } from "../SlugField";

export interface ClassificationStepProps {
  /** `true` al editar: el slug ya está publicado y no se toca. */
  locked: boolean;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * Paso 2 · Clasificación, existencias y activación.
 *
 * ── SOLO lo que Anahí dictó (26-ago, segunda pasada) ────────────────
 * Categoría, tipo de ítem, lo que el tipo pida (composición o
 * promoción), stock y activación. Salieron de aquí "Orden",
 * "Es un paquete mayorista" y las fechas de vigencia de un producto
 * normal: sus datos siguen viajando en el formulario, pero ya no se
 * editan en este paso.
 *
 * ── La categoría y el tipo son CATÁLOGOS, no texto libre ────────────
 * Los dos eran lo peor de cada casa. La categoría era un `<input>` con
 * sugerencias sacadas de los productos que YA existían: con la base
 * vacía no ofrecía nada —no había forma de crear la primera— y con la
 * base llena aceptaba "Detox", "detox" y "DETOX" como tres. El tipo era
 * un `z.enum` cerrado en el código: añadir "promoción" exigía tocar el
 * contrato, el modelo y el panel, y desplegar.
 *
 * Ahora los dos salen de `modules/catalog` y los dos usan el MISMO
 * componente, que además deja crear una opción sin salir del paso. Un
 * `CatalogSelectField` por lista y no dos copias con el `kind` cambiado:
 * el día que el alta necesite una confirmación, la necesita una vez.
 *
 * ── El tipo decide qué se ve en el resto del paso ───────────────────
 * Y decide por su COMPORTAMIENTO, no por su nombre. Es la pieza que
 * hace compatible "la lista de tipos la escribe quien vende" con "el
 * formulario cambia según el tipo": cada opción del catálogo declara si
 * se comporta como producto, como paquete o como promoción, y el paso
 * ramifica sobre esas tres —lista cerrada, bajo control de quien
 * programa— y nunca sobre el valor. Un tipo inventado desde el panel no
 * puede dejar este paso sin saber qué pintar.
 *
 *   producto  → existencias
 *   promoción → existencias + descuento y fechas
 *   paquete   → qué productos lo componen
 *
 * ── Las cuatro opciones de activación, en UN control ────────────────
 * `status`, `isAvailable`, `qualifiesForRank` y `requiresShipping`
 * estaban repartidos por tres pasos con otros nombres. Aquí son un solo
 * selector múltiple, como pidió Anahí. Ver `ProductActivationField`.
 * ═══════════════════════════════════════════════════════════════════
 */
export function ClassificationStep({ locked }: ClassificationStepProps) {
  const { t } = useTranslation();
  const type = (useWatch({ name: "type" }) ?? "") as string;
  const behavior = behaviorOfType(type.trim() || undefined);

  /*
   * Las existencias son de lo que se vende por unidades: un producto y
   * una promoción. Un paquete no lleva su propio inventario —lo llevan
   * sus partes— y contarlo aparte sería una segunda verdad sobre la
   * misma caja.
   */
  const llevaExistencias = behavior === "product" || behavior === "promotion";

  return (
    <FormSectionStack>
      <FormSection
        title={t("Clasificación")}
        /*
          Sin explicación, por el mismo motivo que el bloque de contenido
          del paso 1: lo que decía —"qué es este producto y dónde aparece
          en el catálogo"— lo repite la frase que el paso ya lleva encima
          ("Qué es, cuántas quedan y cómo se comporta en la tienda"), y
          los cuatro campos que vienen debajo se explican solos.
          
          Medido: son 30 px, y son exactamente los que separaban a la
          variante de PROMOCIÓN de caber en una vista de 1440×900 (921).
        */
        // TRES columnas de campos, no dos. Es la otra mitad de apilar las
        // tarjetas: lo que se pierde poniéndolas una debajo de otra se
        // recupera repartiendo los campos dentro de cada una.
        columns={3}
        compact
      >
        <CatalogSelectField
          name="category"
          kind="category"
          label={t("Categoría")}
          required
          /*
            "Elige…" y no "Elige una categoría": el mensaje de error del
            campo vacío es justamente "Elige una categoría", y con el
            mismo texto en el hueco del desplegable el aviso se pierde
            entre dos frases idénticas a dos centímetros una de otra.
          */
          placeholder={t("Elige…")}
          addLabel={t("Añadir categoría")}
        />

        {/*
          El tipo NO es obligatorio, y por eso no lleva asterisco: un
          producto sin tipo se comporta como producto normal
          (`behaviorOfType` cae a "product" cuando no hay valor), que es
          el caso más común del catálogo. Obligarlo sería pedir una
          decisión que la mayoría de las fichas no tiene que tomar.

          La categoría SÍ lo es, porque Anahí lo pidió y porque sin ella
          el producto no aparece bajo ningún filtro de la tienda.
        */}
        <CatalogSelectField
          name="type"
          kind="product_type"
          label={t("Tipo de ítem")}
          placeholder={t("Producto simple")}
          addLabel={t("Añadir tipo")}
        />

        {/*
          La dirección pública ya NO se teclea: se genera del nombre en
          español (Anahí, 26-ago). Este bloque solo se pinta al EDITAR, y
          de solo lectura. Ver `SlugField`.
        */}
        <SlugField locked={locked} />

        {/*
          El descuento y las fechas de una promoción van DENTRO de este
          bloque, no en una tarjeta aparte. Ver `ProductPromotionFields`:
          aparte costaba 126 px de más de los que la pantalla tiene, y
          aquí se lee mejor porque una promoción se define por su
          descuento y su ventana igual que un producto por su categoría.
        */}
        {behavior === "promotion" && <ProductPromotionFields />}
      </FormSection>

      <FormSection
        title={t("Disponibilidad")}
        description={t("Cómo se comporta en la tienda y cuántas quedan.")}
        // Dos celdas como máximo —el desplegable de activación y el
        // stock— así que caben en una sola fila.
        columns={3}
        compact
      >
        <ProductActivationField />

        {llevaExistencias && (
          <NumberField
            name="stock"
            /*
              "Stock (unidades)", que es como Anahí lo nombra en su lista
              de siete pasos. Decía "Existencias (unidades)": el mismo
              campo con otra palabra, y buscar en pantalla un rótulo que
              no coincide con el que se dictó es exactamente el rato que
              cuesta una traducción bienintencionada.
            */
            label={t("Stock (unidades)")}
            hint={t("Vacío = este producto no lleva control de inventario.")}
          />
        )}
      </FormSection>

      {/*
        La composición SÍ va a lo ancho, y sola en su fila: es una lista
        que crece, con un buscador y una cantidad por línea, y en media
        pantalla el nombre de un producto no cabe.
        
        Es además el único caso del paso que puede pasarse de una vista, y
        lo hace por la misma razón que el paso de Beneficios: a partir de
        tres líneas, una lista que crece crece. La alternativa —una lista
        con su propia rueda dentro de la tarjeta— esconde las líneas de
        abajo detrás de un segundo scroll que nadie ve.
      */}
      {behavior === "package" && <ProductPackageItems />}
    </FormSectionStack>
  );
}
